import { FC, useEffect, useState } from 'react';
import { axiosInstance } from '../../../../services/axios';
import { API_SLUGS } from '../../../../constants';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { debounce, filter, isEmpty, lowerCase, startCase } from 'lodash';
import TruncatedScrollingText from '../../../common/truncatedScrollingText';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Plus } from '../../../../icons/plus';
import { IAlertState, useAlertState } from '../../../../store/alertState';

const format = require('string-template');

interface ILinkCloudProjectFormValues {
    group: any;
    project: any;
    api: any;
}

const LinkCloudProjectForm: FC<any> = ({ cloudProjectId, callback, selectedAssets, selectedBranchesData, type, projectName }) => {
    const emptyFormValues = { group: null, project: null, api: null };

    const [formSections, setFormSections] = useState<ILinkCloudProjectFormValues[]>(
        selectedAssets?.length ? [] : [{ ...emptyFormValues }]
    );

    const [groups, setGroups] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [apis, setApis] = useState<any[]>([]);
    const [clickToCreateProject, setClickToCreateProject] = useState("");
    const [clickToCreateGroup, setClickToCreateGroup] = useState("");
    const [clickToCreateAPI, setClickToCreateAPI] = useState("");
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranches, setSelectedBranches] = useState<any[]>([]);
    const { setMessage } = useAlertState(state => state) as IAlertState

    // Fetch groups
    const fetchGroups = async (searchQuery?: string | null) => {
        try {
            const response = await axiosInstance.get(
                `/group?limit=50${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
            );
            setGroups(response.data?.groups || []);
        } catch (error) {
            console.error('Error fetching groups:', error);
            setGroups([]);
        }
    };

    useEffect(()=> {
        fetchBranches();
        setSelectedBranches(selectedBranchesData)
    }, []);

    const fetchBranches = async() => {
        try {
            const path = API_SLUGS.CLOUD_INTEGRATION_BRANCHES +`?type=${type}&projectId=${cloudProjectId}`
            const response = await axiosInstance.get(path);
            setBranches(response?.data?.branches)
        } catch (error) {
            
        }
    }

    const fetchProjects = async (groupId: string, searchQuery?: string | null) => {
        try {
            const response = await axiosInstance.get(
                `${format(API_SLUGS.PROJECTS_BY_GROUP, {
                    groupId,
                })}&limit=50${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
            );
            setProjects(response.data?.projects || []);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
        }
    };

    const fetchAssets = async (projectId: string, searchQuery?: string | null) => {
        try {
            const response = await axiosInstance.get(
                `${format(API_SLUGS.ASSETS, {
                    projectId,
                })}&limit=50${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
            );
            setApis(response.data?.assets || []);
        } catch (error) {
            console.error('Error fetching assets:', error);
            setApis([]);
        }
    };

    const debouncedFetchGroups = debounce(fetchGroups, 300);
    const debouncedFetchProjects = debounce(fetchProjects, 300);
    const debouncedFetchAssets = debounce(fetchAssets, 300);

    useEffect(() => {
        fetchGroups();
        const newSections: ILinkCloudProjectFormValues[] = [];
        if (selectedAssets) {
            for (const asset of selectedAssets) {
                const newValues = {
                    group: {
                        id: asset?.groupId,
                        name: asset?.groupName,
                    },
                    project: {
                        id: asset?.projectId,
                        name: asset?.projectName,
                    },
                    api: {
                        id: asset?.id,
                        name: asset?.name,
                    },
                };

                newSections.push(newValues);
            }

            setFormSections(newSections);
        }
    }, []);

    const handleGroupSearchChange = (event: any) => {
        const searchQuery = event?.target?.value;
        debouncedFetchGroups(searchQuery || null);
    };

    const handleProjectSearchChange = (event: any, groupId: string) => {
        const searchQuery = event?.target?.value;
        if (groupId) debouncedFetchProjects(groupId, searchQuery || null);
    };

    const handleAssetSearchChange = (event: any, projectId: string) => {
        const searchQuery = event?.target?.value;
        if (projectId) debouncedFetchAssets(projectId, searchQuery || null);
    };

    const handleChange = (
        value: any,
        valueType: 'group' | 'project' | 'api',
        index: number
    ) => {
        const updatedSections = [...formSections];
        const updated = { ...updatedSections[index], [valueType]: value };

        if (valueType === 'group') {
            updated.project = null;
            updated.api = null;
            fetchProjects(value?.id);
        } else if (valueType === 'project') {
            updated.api = null;
            fetchAssets(value?.id);
        }

        updatedSections[index] = updated;
        setFormSections(updatedSections);
    };

    const addNewSection = () => {
        setFormSections([...formSections, { ...emptyFormValues }]);
    };

    const removeSection = (index: number) => {
        const updated = [...formSections];
        updated.splice(index, 1);
        setFormSections(updated);
    };

    const handleSave = async () => {
        const assetsToSend = formSections?.map((form) => { return ({ id: form?.api?.id }) });
        const payload = {
            type: type,
            details: {
                projectId: cloudProjectId,
                assets: assetsToSend,
                branches: selectedBranches
            }
        }
        try {
            const response = await axiosInstance.put(API_SLUGS.CLOUD_INTEGRATION_PROJECTS, payload);
            setMessage({ title: "Assets Linked to Project", type: "success" })
            if (callback) {
                callback()
            }
        } catch (error) {
        }
    };

    const addProject = async (name: string, index: number, reroute: boolean = false) => {
        try {
            let projectResponse = await axiosInstance.post(
                API_SLUGS.ADD_PROJECT,
                { name: name, groupId: formSections?.[index]?.group.id }
            );
            await fetchProjects(name);

            setMessage({ title: "Project has been added", type: "success" });
            handleChange(projectResponse.data, "project", index);
        } catch (error: any) {

            setMessage({ title: error.response?.data?.message, type: "error" });

        }
    };

    const addGroup = async (name: string, index: number) => {
        try {
            let groupResponse = await axiosInstance.post(API_SLUGS.ADD_GROUP, {
                name: name,
            });
            await debouncedFetchGroups(name);

            handleChange(groupResponse.data, "group", index);
        } catch (error: any) {
            setMessage({ title: error.response?.data?.message, type: "error" });

        }
    };

    const addApi = async (name: string, index: number) => {
        try {
            let apiResponse = await axiosInstance.post(API_SLUGS.ADD_ASSET, {
                projectId: formSections?.[index]?.project.id,
                name: name,
                type: "API_KIND_OPENAPI",
                assetType: "api",
            });

            handleChange(apiResponse.data, "api", index);

            setMessage({ title: "Api has been added", type: "success" });
        } catch {

        }
    }

    const handleBranchChange = async (value: any) => {
        setSelectedBranches(value)
    }

    return (
        <Grid container spacing={3}>
           
                <Box sx={{ mt:2, width: '100%'}}>
                <Typography variant='h6' sx={{ mt:2, ml:3}}>Select {startCase(type || "")} Branches</Typography>
                <Typography variant='caption' sx={{ ml:3 }}>To make webhook work for all branches, leave branch selection blank. If you select any branch, then webhook will work for only the selected branches of {type}.</Typography>
                
                <Box sx={{display: 'flex', width: '100%',alignItems: 'center', my:2, ml:3}}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <TextField value={projectName} disabled={true} label={`${startCase(type || "")} Project`}/>
                </Box>

               <Box sx={{display: 'flex', flexGrow: 1, alignItems: 'center'}}>
                <Autocomplete
                    sx={{ ml:1, minWidth: '97%' }}
                    size="small"
                    multiple
                    disableClearable
                    clearOnBlur
                    options={branches || []}
                    value={selectedBranches || []}
                    disableCloseOnSelect
                    getOptionLabel={(option: any) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    onChange={(e, value) => handleBranchChange(value)}
                    filterOptions={(options, params) => {
                        return filter(options, item =>
                        lowerCase(item.name).includes(lowerCase(params.inputValue))
                        );
                    }}
                    renderInput={params => (
                        <TextField label={`${startCase(type || "")} Branches`} variant="outlined" {...params} />
                    )}
                    />
                    </Box>
                    </Box>
                </Box>
                <Typography variant='h6' sx={{mt:3, ml:3}}>Select Aptori Asset</Typography>
            {formSections.map((formValues, index) => (
                <Grid item xs={12} key={index} >
                            
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs={4}>
                                    <Autocomplete
                                        sx={{ minWidth: '100%' }}
                                        size="small"
                                        getOptionLabel={(option: any) => option?.name ?? ''}
                                        options={groups || []}
                                        value={formValues.group}
                                        onInputChange={handleGroupSearchChange}
                                        disableClearable
                                        forcePopupIcon={false}
                                        onChange={(e, value) => {
                                            if (!isEmpty(value) && 'id' in value) {
                                                handleChange(value, 'group', index);
                                            }
                                        }}
                                        clearOnBlur
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, item =>
                                                lowerCase(item.name).includes(lowerCase(params.inputValue))
                                            );
                                            if (params.inputValue !== "" && filtered?.length === 0) {
                                                setClickToCreateGroup(params.inputValue);
                                            } else {
                                                setClickToCreateGroup("");
                                            }
                                            return filtered;
                                        }}
                                        renderInput={params => (
                                            <TextField label="Search Group" variant="outlined" {...params} />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        sx={{ minWidth: '100%' }}
                                        size="small"
                                        forcePopupIcon={false}
                                        getOptionLabel={(option: any) => option?.name ?? ''}
                                        options={projects || []}
                                        value={formValues.project}
                                        onInputChange={e => handleProjectSearchChange(e, formValues.group?.id)}
                                        disableClearable
                                        onChange={(e, value) => {
                                            if (!isEmpty(value) && 'id' in value) {
                                                handleChange(value, 'project', index);
                                            }
                                        }}
                                        clearOnBlur
                                        disabled={!formValues?.group}
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, item =>
                                                lowerCase(item?.name).includes(lowerCase(params?.inputValue))
                                            );
                                            if (params?.inputValue !== "" && filtered?.length === 0) {
                                                setClickToCreateProject(params?.inputValue);
                                            } else {
                                                setClickToCreateProject("");
                                            }
                                            return filtered;
                                        }}
                                        renderInput={params => (
                                            <TextField label="Search Project" variant="outlined" {...params} />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <TruncatedScrollingText name={option?.name} maxLength={15} />
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        sx={{ minWidth: '100%' }}
                                        size="small"
                                        forcePopupIcon={false}
                                        getOptionLabel={(option: any) => option?.name ?? ''}
                                        options={apis}
                                        value={formValues.api}
                                        onInputChange={e => handleAssetSearchChange(e, formValues.project?.id)}
                                        disableClearable
                                        disabled={!formValues?.group || !formValues?.project}
                                        onChange={(e, value) => {
                                            if (!isEmpty(value) && 'id' in value) {
                                                handleChange(value, 'api', index);
                                            }
                                        }}
                                        clearOnBlur
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, item =>
                                                lowerCase(item?.name).includes(lowerCase(params?.inputValue))
                                            );
                                            if (params?.inputValue !== "" && filtered?.length === 0) {
                                                setClickToCreateAPI(params.inputValue);
                                            } else {
                                                setClickToCreateAPI("");
                                            }
                                            return filtered;
                                        }}
                                        renderInput={params => (
                                            <TextField label="Search Asset" variant="outlined" {...params} />
                                        )}
                                    />
                                </Grid>
                                
                            </Grid>


                            <Grid container spacing={1} alignItems="center" >
                            <Grid item xs={4}>
                                    {clickToCreateGroup?.trim() !== "" && (
                                        <Alert severity="info" sx={{mt:4}}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography>
                                                    Group named "{clickToCreateGroup}" doesn't exist.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={async () => {
                                                        await addGroup(clickToCreateGroup, index);
                                                        setClickToCreateGroup("");
                                                    }}
                                                >
                                                    Create Now
                                                </Button>
                                            </Box>
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={4}>
                                    {clickToCreateProject?.trim() !== "" && (
                                        <Alert severity="info" sx={{mt:4}}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography>
                                                    Project named "{clickToCreateProject}" doesn't exist.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={async () => {
                                                        await addProject(clickToCreateProject, index);
                                                        setClickToCreateProject("");
                                                    }}
                                                >
                                                    Create Now
                                                </Button>
                                            </Box>
                                        </Alert>
                                    )}
                                </Grid>

                                <Grid item xs={4} >
                                    {clickToCreateAPI?.trim() !== "" && (
                                        <Alert severity="info" sx={{mt:4}}>
                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                <Typography>
                                                    Asset named "{clickToCreateAPI}" doesn't exist.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={async () => {
                                                        await addApi(clickToCreateAPI, index);
                                                        setClickToCreateAPI("");
                                                    }}
                                                >
                                                    Create Now
                                                </Button>
                                            </Box>
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                            {/* <Box display="flex" justifyContent="flex-end" alignItems="center" >
                                <IconButton onClick={() => removeSection(index)} color="error">
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box> */}
                </Grid>
                
            ))}



            <Grid item xs={12}>
                <Button variant="contained" onClick={handleSave}>
                    Save
                </Button>
            </Grid>
        </Grid>
    );
};

export default LinkCloudProjectForm;
